import React, { useContext } from 'react';
import { Navigate, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import routeNameComponentLookup from '|/app/purchase/flow/step-registry';
import { Dashboard, Step, StepName } from '|/app/services';
import ProtectedRoute from '|/components/base/protect-route';
import { ActiveStepContext, CheckItOutContext, FormSuspense } from '|/context';
import SummaryContext from '|/context/summary-context';
import { AppFrameworkContextProvider } from '|/helpers/app-framework';
import StepElement from './step-element';

const CheckoutRedirect: React.FC<{ stepUrl: string }> = ({ stepUrl }) => {
  const { search } = useLocation();
  return <Navigate to={`/checkout${search}#${stepUrl}`} replace />;
};

const getLatestStep = (steps: Step[]) => {
  const orderedSteps = steps.sort((a, b) => a.order - b.order);
  const latestStep = orderedSteps.find(step => step.isEnabled && !step.isComplete);
  return latestStep ?? orderedSteps[0];
};

const defaultProps = {
  dashboard: null as null | Dashboard
};

const Steps = ({ dashboard } = defaultProps) => {
  const { showSummary } = useContext(SummaryContext);
  const { isCheckItOutTreatment } = useContext(CheckItOutContext);
  const navigate = useNavigate();

  const { steps = [] } = dashboard ?? {};

  if (steps.length === 0) return null;

  const latestStep = getLatestStep(steps);

  return (
    <>
      <ActiveStepContext.Provider value={{ activeStepUrl: latestStep?.url }}>
        <AppFrameworkContextProvider>
          <FormSuspense>
            <Routes>
              {steps.map(step => {
                const { id = null, section = 0, order = 0, isEnabled = false } = step ?? {};
                const { section: latestSection = 0, order: latestOrder = 0 } = latestStep ?? {};

                const stepConfig = id === null ? null : routeNameComponentLookup[id];

                if (section === 2 && id !== StepName.Insurance && isCheckItOutTreatment) {
                  return (
                    <Route
                      path={`${step.url}/*`}
                      key={step.name}
                      element={<CheckoutRedirect stepUrl={step.url} />}
                    />
                  );
                }

                const shouldRedirect =
                  id === null ||
                  stepConfig === null ||
                  (!isCheckItOutTreatment && section !== latestSection) ||
                  order > latestOrder ||
                  !isEnabled;

                if (shouldRedirect) {
                  return null;
                }

                return (
                  <Route
                    path={`${step.url}/*`}
                    key={step.name}
                    element={
                      <StepElement
                        step={step}
                        isHidden={showSummary}
                        GetDashboardAndRouteToNextStep={() => {
                          navigate('/next-step');
                        }}
                      />
                    }
                  />
                );
              })}
              <Route
                path="/"
                element={
                  <ProtectedRoute>
                    <></>
                  </ProtectedRoute>
                }
              />
              {isCheckItOutTreatment && (
                <Route
                  path="drivers-license/*"
                  element={<CheckoutRedirect stepUrl="buyer-identity" />}
                />
              )}
              <Route
                path="*"
                element={
                  <ProtectedRoute>
                    <Navigate to="/next-step" replace={true} />
                  </ProtectedRoute>
                }
              />
            </Routes>
          </FormSuspense>
        </AppFrameworkContextProvider>
      </ActiveStepContext.Provider>
    </>
  );
};

export default Steps;
export { StepElement };
